import { HttpService } from '@skylab-shared/core';
import { EventNotification, EventNotificationsServiceBase, EventNotificationType } from '../event-notifications.service-base';

type NotificatorObserver<TData> = (data: TData) => void;

export abstract class NotificatorBase<TRequestData, TData = TRequestData> {
    private observers : NotificatorObserver<TData>[] = [];

    protected constructor(
        private readonly eventNotifications: EventNotificationsServiceBase,
        protected readonly http: HttpService
    ) {
        this.init();
    }

    private init(): void {
        this.eventNotifications.addObserver(this.onEventsChangedAsync.bind(this) as any);
    }

    protected async onEventsChangedAsync(events: EventNotification<TRequestData>[]): Promise<void> {
        const targetedEvents = this.filterEvents(events);
        if (targetedEvents.length === 0 || this.observers.length === 0) return;

        const prepared = await this.prepareDataAsync(targetedEvents);
        this.notifyObservers(prepared);
    }

    protected filterEvents(events: EventNotification<TRequestData>[]): EventNotification<TRequestData>[] {
        return Array.isArray(this.eventType)
            ? events.filter(elem => this.eventType.includes(elem.kind))
            : events.filter(elem => elem.kind === this.eventType);
    }

    protected notifyObservers(data: TData): void {
        this.observers.forEach(observer => observer(data));
    }

    public addObserver(observer: NotificatorObserver<TData>): () => void {
        this.observers.push(observer);
        return () => {
            const index = this.observers.indexOf(observer);
            if (index !== -1) this.observers.splice(index, 1);
        };
    }

    protected abstract eventType: EventNotificationType | EventNotificationType[];
    protected abstract prepareDataAsync(data: EventNotification[]): Promise<TData>;
}
